import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { computeBounds } from "../lib/runAnalytics";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || "";

const DARK_STYLE = "mapbox://styles/mapbox/dark-v11";
const NEON_GREEN = "#39FF14";

/**
 * RunMap — renders a Mapbox GL JS map with a neon-green polyline traced
 * from `gpsPath` ({lat,lng,t,alt?}[]).
 *
 *   mode="live"     → auto-pans to the leading point of the path; map grows.
 *   mode="summary"  → does NOT pan; calls fitBounds() once on the full path.
 */
export default function RunMap({
    gpsPath = [],
    mode = "summary",
    height = 280,
    className = "",
    onReady,
}) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const sourceReadyRef = useRef(false);
    const fitDoneRef = useRef(false);

    // Init map once
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        if (!mapboxgl.accessToken) {
            // eslint-disable-next-line no-console
            console.warn("REACT_APP_MAPBOX_TOKEN missing — map will not render");
            return;
        }
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: DARK_STYLE,
            center: gpsPath[0] ? [gpsPath[0].lng, gpsPath[0].lat] : [-0.1278, 51.5074],
            zoom: gpsPath[0] ? 15 : 11,
            preserveDrawingBuffer: true, // required for canvas snapshots / PNG export
            attributionControl: false,
        });
        mapRef.current = map;

        map.on("load", () => {
            map.addSource("run-route", {
                type: "geojson",
                data: {
                    type: "Feature",
                    properties: {},
                    geometry: { type: "LineString", coordinates: [] },
                },
            });
            // Glow underlay
            map.addLayer({
                id: "run-route-glow",
                type: "line",
                source: "run-route",
                layout: { "line-join": "round", "line-cap": "round" },
                paint: {
                    "line-color": NEON_GREEN,
                    "line-width": 12,
                    "line-opacity": 0.25,
                    "line-blur": 8,
                },
            });
            // Main line
            map.addLayer({
                id: "run-route-line",
                type: "line",
                source: "run-route",
                layout: { "line-join": "round", "line-cap": "round" },
                paint: { "line-color": NEON_GREEN, "line-width": 4 },
            });
            // Start + finish markers source
            map.addSource("run-endpoints", {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] },
            });
            map.addLayer({
                id: "run-endpoint-circle",
                type: "circle",
                source: "run-endpoints",
                paint: {
                    "circle-radius": 7,
                    "circle-color": [
                        "match",
                        ["get", "kind"],
                        "start", "#ffffff",
                        "finish", "#FF3B30",
                        "current", NEON_GREEN,
                        "#ffffff",
                    ],
                    "circle-stroke-color": "#0d0d0d",
                    "circle-stroke-width": 3,
                },
            });
            sourceReadyRef.current = true;
            if (onReady) onReady(map);
            // Force a data update + fit on initial load
            updatePath();
        });

        return () => {
            try { map.remove(); } catch (e) { /* ignore */ }
            mapRef.current = null;
            sourceReadyRef.current = false;
            fitDoneRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update path whenever it changes
    const updatePath = () => {
        const map = mapRef.current;
        if (!map || !sourceReadyRef.current) return;
        const coords = gpsPath.map((p) => [p.lng, p.lat]);
        const src = map.getSource("run-route");
        if (src) {
            src.setData({
                type: "Feature",
                properties: {},
                geometry: { type: "LineString", coordinates: coords },
            });
        }
        // endpoints
        const ep = map.getSource("run-endpoints");
        if (ep && gpsPath.length > 0) {
            const first = gpsPath[0];
            const last = gpsPath[gpsPath.length - 1];
            const features = [
                {
                    type: "Feature",
                    properties: { kind: "start" },
                    geometry: { type: "Point", coordinates: [first.lng, first.lat] },
                },
            ];
            if (gpsPath.length > 1) {
                features.push({
                    type: "Feature",
                    properties: { kind: mode === "live" ? "current" : "finish" },
                    geometry: { type: "Point", coordinates: [last.lng, last.lat] },
                });
            }
            ep.setData({ type: "FeatureCollection", features });
        }
        if (mode === "live" && gpsPath.length > 0) {
            const last = gpsPath[gpsPath.length - 1];
            map.easeTo({ center: [last.lng, last.lat], zoom: Math.max(map.getZoom(), 15), duration: 600 });
        } else if (mode === "summary" && gpsPath.length > 1 && !fitDoneRef.current) {
            const b = computeBounds(gpsPath);
            if (b) {
                map.fitBounds(b, { padding: 36, duration: 800, maxZoom: 16 });
                fitDoneRef.current = true;
            }
        }
    };

    useEffect(() => {
        updatePath();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gpsPath]);

    if (!mapboxgl.accessToken) {
        return (
            <div
                className={`surface-1 flex items-center justify-center text-xs text-zinc-500 ${className}`}
                style={{ height }}
            >
                Map unavailable — REACT_APP_MAPBOX_TOKEN missing.
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            data-testid="run-map"
            className={className}
            style={{ width: "100%", height, borderRadius: 6, overflow: "hidden", border: "1px solid #262626" }}
        />
    );
}
